<template>
  <div class="fill-height">
    <div v-if="profile" class="fill-height">
      <v-sheet dark color="transparent" class="fill-height pa-4" style="overflow-y: auto">
        <v-container class="profile-container">
          <v-row v-if="!isOwnProfile" class="mb-4">
            <v-col class="py-0">
              <v-btn text small @click="$emit('close-profile')">
                <v-icon left>mdi-arrow-left</v-icon>
                Zurück
              </v-btn>
            </v-col>
          </v-row>
          <div class="text-center">
            <div style="position: relative; display: inline-block">
              <user-profilepicture
                class="mt-5"
                @updatedPicture="updateUserPicture"
                :width="120"
                :height="120"
                v-if="isOwnProfile"
                :picture="profile.user.bild"></user-profilepicture>
              <v-avatar v-else size="120" class="elevation-6 avatar-style">
                <v-img :src="profile.user.bild ? '/img/profilbilder/' + profile.user.bild : '/img/profilbilder/uploads/1741100494649.png'"></v-img>
              </v-avatar>
            </div>
            <h1 class="text-h5 font-weight-bold mt-4">
              {{ profile.user.vorname }} {{ profile.user.nachname }} <span v-if="profile.user.age">({{ profile.user.age }})</span>
            </h1>
            <p v-if="profile.username" class="username-style mt-n1">@{{ profile.username }}</p>

            <div class="my-6">
              <div v-if="isOwnProfile" class="d-flex justify-center">
                <v-btn large rounded outlined @click="openEditDialog"> <v-icon left>mdi-pencil-outline</v-icon> Profil bearbeiten </v-btn>
              </div>

              <div v-else>
                <v-alert v-if="profile.pendingTrainingRequest" border="left" colored-border color="primary" elevation="2" class="text-left mb-6">
                  <h4 class="primary--text mb-2">Anfrage gesendet</h4>
                  <p class="body-2 mb-3">
                    Du hast {{ profile.user.vorname }} für ein Training an folgenden Tagen angefragt:
                    <strong>{{ profile.pendingTrainingRequest.availability.days.join(", ") }}</strong>
                    ({{ profile.pendingTrainingRequest.availability.timeOfDay }})
                  </p>
                  <v-btn small outlined color="white" @click="withdrawTrainingRequest(profile.pendingTrainingRequest._id)">
                    Anfrage zurückziehen
                  </v-btn>
                </v-alert>

                <div v-else>
                  <v-alert
                    v-if="profile.relationships && profile.relationships.friend === 'pending' && profile.relationships.isInitiator"
                    border="left"
                    colored-border
                    color="primary"
                    elevation="2"
                    class="text-left mb-6">
                    <h4 class="primary--text mb-2">Anfrage ausstehend</h4>
                    <p class="body-2 mb-3">Du hast {{ profile.user.vorname }} eine Freundschaftsanfrage gesendet.</p>
                    <v-btn small outlined color="white" :loading="actionLoading && activeAction === 'friend'" @click="withdrawFriendRequest">
                      Anfrage zurückziehen
                    </v-btn>
                  </v-alert>

                  <div v-else>
                    <div v-if="!profile.relationships || !profile.relationships.blocked" class="action-deck">
                      <v-btn
                        rounded
                        :color="friendButtonColor"
                        :disabled="profile.relationships && profile.relationships.friend === 'pending' && profile.relationships.isInitiator"
                        :loading="actionLoading && activeAction === 'friend'"
                        @click="handleFriendAction">
                        <v-icon>{{ friendButtonIcon }}</v-icon>
                      </v-btn>

                      <v-btn
                        v-if="
                          !profile.isLimited &&
                          !profile.relationships?.isTrainingPartner &&
                          !profile.pendingTrainingRequest &&
                          profile?.privacy?.allowTrainingRequests
                        "
                        rounded
                        outlined
                        color="white"
                        @click="handleTrainingAction"
                        :loading="actionLoading && activeAction === 'training'">
                        <v-icon>mdi-dumbbell</v-icon>
                      </v-btn>

                      <v-menu top offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn rounded color="grey darken-2" v-bind="attrs" v-on="on">
                            <v-icon>mdi-dots-horizontal</v-icon>
                          </v-btn>
                        </template>
                        <v-list dark dense>
                          <v-list-item @click="blockUser">
                            <v-list-item-icon><v-icon color="error">mdi-block-helper</v-icon></v-list-item-icon>
                            <v-list-item-title>Benutzer blockieren</v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="profile.relationships && profile.relationships.isTrainingPartner" class="crew-card-container text-left mt-4">
            <div class="crew-card__glow"></div>
            <div class="crew-card__content">
              <div class="d-flex align-center mb-3">
                <div class="crew-card__icon-wrapper mr-3">
                  <v-icon class="crew-card__icon">mdi-dumbbell</v-icon>
                </div>
                <div>
                  <h3 class="crew-card__title mb-0">LIFTING PARTNER</h3>
                </div>
                <v-spacer></v-spacer>
                <v-menu offset-y left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" color="white"><v-icon>mdi-dots-vertical</v-icon></v-btn>
                  </template>
                  <v-list dark dense>
                    <v-list-item @click="openDirectRequestDialog">
                      <v-list-item-icon><v-icon small>mdi-calendar-sync-outline</v-icon></v-list-item-icon>
                      <v-list-item-title>Plan ändern</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="confirmEndPartnership">
                      <v-list-item-icon><v-icon small color="error">mdi-account-cancel-outline</v-icon></v-list-item-icon>
                      <v-list-item-title>Partnerschaft beenden</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>

              <div class="crew-card__divider"></div>

              <div v-if="profile.relationships.trainingAvailability" class="mt-4">
                <p class="crew-card__plan-label">Trainingszeiten:</p>
                <v-chip-group active-class="primary--text">
                  <v-chip
                    v-for="day in profile.relationships.trainingAvailability.days"
                    :key="day"
                    color="rgba(255,255,255,0.1)"
                    text-color="white"
                    class="crew-chip"
                    label>
                    <v-icon left small>mdi-calendar-check</v-icon> {{ day }}
                  </v-chip>
                  <v-chip color="rgba(255,255,255,0.1)" text-color="white" class="crew-chip" label>
                    <v-icon left small>mdi-clock-outline</v-icon> {{ profile.relationships.trainingAvailability.timeOfDay }}
                  </v-chip>
                </v-chip-group>
              </div>
            </div>
          </div>

          <v-row v-if="!profile.relationships || !profile.relationships.blocked" class="my-6 text-center">
            <v-col>
              <div class="stat">
                <span class="font-weight-bold text-h5">{{ profile.friendCount || 0 }}</span>
                <p class="text-caption mb-0" style="color: #f7f7f4a0">Freunde</p>
              </div>
            </v-col>
            <v-col v-if="!profile.isLimited">
              <div class="stat">
                <span class="font-weight-bold text-h5">{{ profile.streaks ? profile.streaks.current : 0 }}</span>
                <p class="text-caption mb-0" style="color: #f7f7f4a0">Streak</p>
              </div>
            </v-col>
            <v-col v-if="!profile.isLimited">
              <div class="stat">
                <v-icon size="28" :color="profile.status === 'in_training' ? '#F7B5A7' : '#C9EEF2'">{{ statusIcon(profile.status) }}</v-icon>
                <p class="text-caption mb-0" style="color: #f7f7f4a0">{{ statusText(profile.status) }}</p>
              </div>
            </v-col>
          </v-row>

          <p v-if="!profile.relationships || !profile.relationships.blocked" class="body-1 text-center my-6">
            {{ profile.bio || "Keine Biografie vorhanden." }}
          </p>

          <v-divider v-if="!profile.relationships || (!profile.relationships.blocked && !profile.isLimited)" class="my-6" dark></v-divider>

          <div v-if="!profile.isLimited && (!profile.relationships || !profile.relationships.blocked) && displayHighlights.length > 0" class="mb-2">
            <div class="highlights-container">
              <div
                v-for="highlight in displayHighlights"
                :key="highlight.key"
                class="highlight-bubble-wrapper"
                @click="isOwnProfile ? openHighlightEditDialog(highlight) : showHighlightDetails(highlight)">
                <div class="highlight-bubble" :class="{ 'is-addable': isOwnProfile && !highlight.content }">
                  <v-icon color="#f7f7f4">{{ highlight.icon }}</v-icon>
                </div>
                <p class="highlight-label">{{ highlight.label }}</p>
              </div>
            </div>
            <v-divider class="mt-6" dark></v-divider>
          </div>

          <div v-if="!profile.isLimited && (!profile.relationships || !profile.relationships.blocked)">
            <v-tabs v-model="tab" background-color="transparent" grow icons-and-text centered class="content-tabs">
              <v-tab href="#activity"><v-icon class="mr-2">mdi-newspaper-variant-outline</v-icon> Beiträge</v-tab>
              <v-tab href="#friends"><v-icon class="mr-2">mdi-account-group-outline</v-icon> Freunde</v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab" class="transparent mt-6">
              <v-tab-item value="activity">
                <div v-if="postsLoading" class="text-center pa-8"><v-progress-circular indeterminate color="primary"></v-progress-circular></div>
                <div v-else-if="userPosts.length > 0">
                  <FeedItemCard
                    :inProfile="true"
                    v-for="post in userPosts"
                    :key="post._id"
                    :item="post"
                    class="mb-6"
                    @delete-post="confirmDeletePost" />
                </div>
                <div v-else class="text-center pa-8 white--text">
                  <v-icon color="#f7f7f4a0" size="60" class="mb-3">mdi-history</v-icon>
                  <p style="color: #f7f7f4a0">Noch keine Beiträge vorhanden.</p>
                </div>
              </v-tab-item>
              <v-tab-item value="friends">
                <div v-if="friendsLoading" class="text-center pa-8"><v-progress-circular indeterminate color="#F7B5A7"></v-progress-circular></div>
                <FriendListComponent v-else-if="friendsList.length > 0" :friends="friendsList" @view-profile="onViewProfile" />
                <div v-else class="text-center pa-8" style="color: #f7f7f4a0"><p>Dieser Benutzer hat noch keine Freunde.</p></div>
              </v-tab-item>
            </v-tabs-items>
          </div>

          <div v-else-if="profile.relationships?.blocked || profile.isLimited" class="text-center pa-md-8 pa-4 white--text">
            <div v-if="profile.relationships?.blocked">
              <v-icon color="#F7F7F4" size="80" class="mb-4">mdi-block-helper</v-icon>
              <h3 class="text-h6 font-weight-medium">Benutzer blockiert</h3>
              <p class="body-1 mt-2" style="color: #f7f7f4a0">Du kannst die Details dieses Profils nicht sehen.</p>
            </div>
            <div v-else-if="profile.isLimited">
              <v-icon color="#F7F7F4" size="80" class="mb-4">mdi-lock-outline</v-icon>
              <h3 class="text-h6 font-weight-medium">
                {{ profile.privacySetting === "private" ? "Dieses Profil ist privat" : "Profil nur für Freunde" }}
              </h3>
              <p class="body-1 mt-2" style="color: #f7f7f4a0">Werde ein Freund, um die vollständigen Details und Aktivitäten zu sehen.</p>

              <div class="mt-6">
                <div
                  v-if="
                    !profile.relationships || (profile.relationships.friend !== 'pending_outgoing' && profile.relationships.friend !== 'pending')
                  ">
                  <v-btn
                    rounded
                    large
                    color="#F7B5A7"
                    class="font-weight-bold"
                    :loading="actionLoading && activeAction === 'friend'"
                    @click="addFriend">
                    <v-icon left>mdi-account-plus-outline</v-icon>
                    Freundschaftsanfrage senden
                  </v-btn>
                </div>
                <div v-else>
                  <v-btn rounded large disabled text>
                    <v-icon left>mdi-account-clock</v-icon>
                    Anfrage gesendet
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </v-container>
      </v-sheet>
    </div>

    <div v-else class="d-flex align-center justify-center fill-height white--text">
      <v-progress-circular v-if="loading" indeterminate color="#F7B5A7" size="48"></v-progress-circular>
      <div v-else-if="profileNotFound && isOwnProfile" class="text-center">
        <v-icon color="#F7F7F4" size="80" class="mb-4">mdi-account-plus-outline</v-icon>
        <h3 class="text-h6 font-weight-medium">Erstelle dein soziales Profil</h3>
        <p class="body-1 mt-2 px-4" style="color: #f7f7f4a0">Teile deine Interessen und finde Trainingspartner.</p>
        <v-btn color="primary white--text" class="mt-4 black--text font-weight-bold" rounded @click="openCreateDialog">Profil jetzt erstellen</v-btn>
      </div>
      <div v-else class="text-center">
        <v-icon color="#f7f7f4a0" size="60" class="mb-3">mdi-alert-circle-outline</v-icon>
        <p style="color: #f7f7f4a0">
          {{ profileNotFound ? "Dieses Profil existiert nicht oder ist privat." : "Profil konnte nicht geladen werden." }}
        </p>
      </div>
    </div>

    <v-dialog v-model="profileDialog" fullscreen persistent transition="dialog-bottom-transition">
      <v-card color="#232929" dark>
        <v-toolbar dark color="#232929" flat>
          <v-btn icon @click="profileDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title>{{ profileData._id ? "Profil bearbeiten" : "Profil erstellen" }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn color="#01734c" :loading="actionLoading && activeAction === 'profile'" @click="submitProfile" class="font-weight-bold"
            >Speichern</v-btn
          >
        </v-toolbar>
        <v-container class="py-6">
          <v-subheader>Allgemein</v-subheader>
          <v-text-field
            filled
            rounded
            hide-details
            v-model="profileData.username"
            label="Benutzername"
            prepend-inner-icon="mdi-at"
            hint="Muss einzigartig sein"
            persistent-hint></v-text-field>
          <v-textarea
            filled
            hide-details
            rounded
            v-model="profileData.bio"
            label="Über mich (Bio)"
            prepend-inner-icon="mdi-card-account-details-outline"
            rows="3"
            class="mt-4"
            hint="Erzähl etwas über dich (max. 150 Zeichen)"></v-textarea>
          <v-select
            filled
            hide-details
            rounded
            v-model="profileData.status"
            :items="[
              { text: 'Verfügbar', value: 'available' },
              { text: 'Im Training', value: 'in_training' },
              { text: 'Nicht stören', value: 'do_not_disturb' },
            ]"
            label="Dein aktueller Status"
            prepend-inner-icon="mdi-circle-slice-8"
            class="mt-4"></v-select>
          <v-subheader class="mt-4">Privatsphäre</v-subheader>
          <v-select
            filled
            rounded
            hide-details
            v-model="profileData.privacy.postsVisibility"
            :items="[
              { text: 'Öffentlich (Alle)', value: 'public' },
              { text: 'Nur Freunde', value: 'friends' },
            ]"
            label="Wer kann deine Beiträge sehen?"
            prepend-inner-icon="mdi-newspaper-variant-multiple"
            class="mt-2"></v-select>
          <v-switch hide-details v-model="profileData.privacy.showAge" label="Alter im Profil anzeigen" color="#01734c" class="mt-2"></v-switch>
          <v-switch hide-details v-model="profileData.privacy.showInCheckinPool" label="Im 'Aktuell im Gym'-Pool anzeigen" color="#01734c"></v-switch>
          <v-switch
            hide-details
            v-model="profileData.privacy.allowTrainingRequests"
            label="Trainingspartnerschaften erlauben"
            color="#01734c"></v-switch>
        </v-container>
      </v-card>
    </v-dialog>
    <v-dialog v-model="highlightDialog" max-width="380" content-class="tiktok-dialog-container" transition="dialog-fade-transition">
      <div class="tiktok-dialog-card">
        <div class="dialog-glow-border"></div>
        <v-btn icon dark class="close-button" @click="highlightDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        <div class="dialog-content text-center">
          <v-icon class="dialog-icon">{{ selectedHighlight.icon }}</v-icon>
          <h3 class="dialog-title">{{ selectedHighlight.title }}</h3>
          <p class="dialog-main-text">
            {{
              isArray(selectedHighlight.content)
                ? selectedHighlight.content.join(", ")
                : selectedHighlight.content || "Keine Informationen verfügbar."
            }}
          </p>
        </div>
      </div>
    </v-dialog>
    <v-dialog v-model="editHighlightDialog" max-width="420" persistent content-class="tiktok-dialog-container" transition="dialog-fade-transition">
      <div class="tiktok-dialog-card edit-dialog">
        <div class="dialog-glow-border"></div>
        <div class="edit-dialog-title">{{ selectedHighlight.title }} bearbeiten</div>
        <div class="edit-dialog-content">
          <v-combobox
            v-if="selectedHighlight.type === 'combobox'"
            v-model="editHighlightData"
            :label="selectedHighlight.label"
            multiple
            chips
            deletable-chips
            dark
            filled
            rounded
            hide-details="auto"
            hint="Eintrag eingeben und Enter drücken"
            persistent-hint
            class="styled-input"></v-combobox>
          <v-text-field
            v-if="selectedHighlight.type === 'textfield'"
            v-model="editHighlightData"
            :label="selectedHighlight.label"
            dark
            filled
            rounded
            hide-details="auto"
            class="styled-input"></v-text-field>
        </div>
        <div class="edit-dialog-actions">
          <v-btn class="white--text" text @click="editHighlightDialog = false">Abbrechen</v-btn>
          <v-btn color="#01734c" class="font-weight-bold white--text" :loading="actionLoading && activeAction === 'highlight'" @click="saveHighlight"
            >Speichern</v-btn
          >
        </div>
      </div>
    </v-dialog>
    <TrainingRequestDialog v-model="trainingRequestDialog" :user="selectedUserForRequest" @sent="onTrainingRequestSent" />
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import FriendListComponent from "./FriendListComponent.vue"
import FeedItemCard from "./FeedItemCard.vue"
import UserProfilepicture from "@/views/user/components/UserProfilepicture.vue"
import TrainingRequestDialog from "./TrainingRequestDialog.vue"

export default {
  components: { FriendListComponent, FeedItemCard, UserProfilepicture, TrainingRequestDialog },
  props: {
    userId: { type: String, required: true },
  },
  data: () => ({
    userPosts: [],
    postsLoading: false,
    tab: "activity",
    friendsList: [],
    friendsLoading: false,
    userService,
    profile: null,
    loading: false,
    actionLoading: false,
    activeAction: null, // Um zu wissen, welcher Button den Ladespinner zeigt
    profileNotFound: false,
    profileDialog: false,
    profileData: {
      username: "",
      bio: "",
      interests: [],
      trainingGoals: [],
      favoriteEquipment: "",
      trainingTimes: "",
      lookingFor: "",
      status: "available",
      privacy: {
        postsVisibility: "friends",
        showAge: true,
        isSearchable: true,
        showInCheckinPool: true,
        allowTrainingRequests: true,
      },
    },
    highlightDialog: false,
    selectedHighlight: { title: "", icon: "", content: "", label: "" },
    editHighlightDialog: false,
    editHighlightData: null,
    trainingRequestDialog: false,
    selectedUserForRequest: null,
  }),
  computed: {
    trainingButtonIcon() {
      return "mdi-dumbbell"
    },
    trainingButtonText() {
      return "Training"
    },
    trainingButtonColor() {
      return "#F7B5A7"
    },
    friendButtonIcon() {
      if (this.profile?.relationships?.friend === "pending_incoming") {
        return "mdi-account-plus"
      }
      switch (this.profile?.relationships?.friend) {
        case "accepted":
          return "mdi-account-check"
        case "pending_outgoing":
          return "mdi-account-clock"
        default:
          return "mdi-account-plus-outline"
      }
    },
    friendButtonColor() {
      switch (this.profile?.relationships?.friend) {
        case "accepted":
          return "success"
        case "pending_incoming":
          return "info"
        default:
          return "#F7B5A7" // Peach
      }
    },
    trainingButtonIcon() {
      return "mdi-dumbbell"
    },
    trainingButtonColor() {
      return this.profile?.relationships?.isTrainingPartner ? "primary" : "grey darken-2"
    },
    displayHighlights() {
      if (!this.profile) return []
      const allHighlightsConfig = [
        { key: "interests", title: "Meine Interessen", label: "Interessen", defaultIcon: "mdi-heart-multiple-outline", type: "combobox" },
        { key: "trainingGoals", title: "Meine Trainingsziele", label: "Ziele", defaultIcon: "mdi-flag-checkered", type: "combobox" },
        { key: "favoriteEquipment", title: "Mein Lieblingsgerät", label: "Gerät", defaultIcon: "mdi-dumbbell", type: "textfield" },
        { key: "trainingTimes", title: "Meine Trainingszeiten", label: "Zeiten", defaultIcon: "mdi-clock-time-eight-outline", type: "textfield" },
        { key: "lookingFor", title: "Ich suche nach", label: "Suche", defaultIcon: "mdi-account-search-outline", type: "textfield" },
      ]
      if (this.isOwnProfile) {
        return allHighlightsConfig.map((config) => {
          const content = this.profile[config.key]
          const hasContent = Array.isArray(content) ? content.length > 0 : !!content
          return { ...config, content: content, icon: hasContent ? config.defaultIcon : "mdi-plus" }
        })
      }
      return allHighlightsConfig
        .map((config) => ({ ...config, content: this.profile[config.key], icon: config.defaultIcon }))
        .filter((item) => (Array.isArray(item.content) ? item.content.length > 0 : !!item.content))
    },
    loggedInUserId() {
      return userService.getUserId()
    },
    isOwnProfile() {
      return this.loggedInUserId === this.userId
    },
  },
  methods: {
    async withdrawFriendRequest() {
      if (!confirm("Möchtest du die Freundschaftsanfrage wirklich zurückziehen?")) return
      this.activeAction = "friend"
      this.actionLoading = true
      try {
        await api.social.removeOrDeclineRelationship(this.profile.relationships.friendshipId)
        this.$toast.success("Anfrage zurückgezogen.")
        await this.loadProfile()
      } catch (error) {
        alert("Aktion fehlgeschlagen.")
      } finally {
        this.actionLoading = false
        this.activeAction = null
      }
    },
    handleFriendAction() {
      this.activeAction = "friend"

      const rel = this.profile.relationships

      // Fall 1: Es existiert eine Beziehung
      if (rel) {
        if (rel.friend === "accepted") {
          this.removeFriend()
        } else if (rel.friend === "pending" && !rel.isInitiator) {
          // Nur wenn eine Anfrage 'pending' ist UND ich NICHT der Initiator bin, kann ich sie annehmen.
          this.acceptFriendRequest()
        }
        // Wenn 'pending' und ich der Initiator bin, passiert nichts, da der Button deaktiviert ist.
      }
      // Fall 2: Es existiert noch KEINE Beziehung
      else {
        this.addFriend()
      }
    },
    async withdrawTrainingRequest(requestId) {
      if (!confirm("Möchtest du diese Trainingsanfrage wirklich zurückziehen?")) return
      try {
        await api.social.withdrawTrainingRequest(requestId)
        this.$toast.success("Anfrage zurückgezogen.")
        this.loadProfile()
      } catch (error) {
        alert("Aktion fehlgeschlagen.")
      }
    },
    handleTrainingAction() {
      // Diese Methode wird jetzt nur noch aufgerufen, wenn man KEIN Partner ist.
      this.activeAction = "training"
      this.openDirectRequestDialog()
    },

    openDirectRequestDialog() {
      // Diese Methode wird jetzt für neue Anfragen UND zum Bearbeiten genutzt.
      this.selectedUserForRequest = { _id: this.profile.user._id, vorname: this.profile.user.vorname }
      this.trainingRequestDialog = true
    },

    onTrainingRequestSent() {
      this.$toast.success("Deine Trainingsanfrage wurde versendet!")
      // Lade das Profil neu, falls sich durch die Anfrage etwas ändert
      this.loadProfile()
    },
    confirmEndPartnership() {
      if (
        confirm(`Möchtest du die Trainingspartnerschaft mit ${this.profile.user.vorname} wirklich beenden? Eure Freundschaft bleibt davon unberührt.`)
      ) {
        this.endTrainingPartnership()
      }
    },

    async endTrainingPartnership() {
      const relationshipId = this.profile.relationships.friendshipId
      if (!relationshipId) return

      this.activeAction = "training"
      this.actionLoading = true
      try {
        // Der Backend-Aufruf, den wir bereits definiert haben
        await api.social.endTrainingPartnership(relationshipId)
        this.$toast.success("Trainingspartnerschaft beendet.")
        await this.loadProfile() // Profil neu laden, um den Button-Zustand zu aktualisieren
      } catch (error) {
        console.log("Fehler beim Beenden der Trainingspartnerschaft:", error)
        alert("Aktion fehlgeschlagen.")
      } finally {
        this.actionLoading = false
        this.activeAction = null
      }
    },
    async updateTrainingPartnership(action) {
      const relationshipId = this.profile.relationships.friendshipId
      if (!relationshipId) {
        alert("Du musst zuerst befreundet sein.")
        return
      }
      this.actionLoading = true
      try {
        await api.social.updateTrainingPartnership(relationshipId, action)
        await this.loadProfile()
      } catch (error) {
        alert("Aktion fehlgeschlagen.")
      } finally {
        this.actionLoading = false
        this.activeAction = null
      }
    },
    async addFriend() {
      this.actionLoading = true
      try {
        await api.social.sendFriendRequest(this.userId)
        await this.loadProfile()
      } catch (error) {
        alert(error.response?.data?.message || "Anfrage fehlgeschlagen.")
      } finally {
        this.actionLoading = false
        this.activeAction = null
      }
    },
    async acceptFriendRequest() {
      const relationshipId = this.profile.relationships.friendshipId
      if (!relationshipId) return
      this.actionLoading = true
      try {
        await api.social.acceptRequest(relationshipId)
        await this.loadProfile()
      } catch (error) {
        alert("Annehmen der Freundschaft fehlgeschlagen.")
      } finally {
        this.actionLoading = false
        this.activeAction = null
      }
    },
    async removeFriend() {
      if (!confirm("Bist du sicher, dass du diese Freundschaft beenden möchtest? Dies beendet auch eine eventuelle Trainingspartnerschaft.")) return
      const relationshipId = this.profile.relationships.friendshipId
      if (!relationshipId) return
      this.actionLoading = true
      try {
        await api.social.removeOrDeclineRelationship(relationshipId)
        await this.loadProfile()
      } catch (error) {
        alert("Freundschaft konnte nicht beendet werden.")
      } finally {
        this.actionLoading = false
        this.activeAction = null
      }
    },
    async blockUser() {
      if (!confirm(`Bist du sicher, dass du ${this.profile.user.vorname} blockieren möchtest?`)) return
      this.activeAction = "block"
      this.actionLoading = true
      try {
        await api.social.blockUser(this.userId)
        this.$emit("close-profile")
      } catch (error) {
        alert("Blockieren fehlgeschlagen.")
      } finally {
        this.actionLoading = false
        this.activeAction = null
      }
    },
    updateUserPicture(name) {
      this.profile.user.bild = name
    },
    isArray(value) {
      return Array.isArray(value)
    },
    showHighlightDetails(highlight) {
      this.selectedHighlight = highlight
      this.highlightDialog = true
    },
    openHighlightEditDialog(highlight) {
      this.selectedHighlight = highlight
      this.editHighlightData = Array.isArray(highlight.content) ? [...(highlight.content || [])] : highlight.content || ""
      this.editHighlightDialog = true
    },
    async saveHighlight() {
      this.activeAction = "highlight"
      this.actionLoading = true
      console.log(this.selectedHighlight.key, this.editHighlightData)
      try {
        const payload = { [this.selectedHighlight.key]: this.editHighlightData }
        await api.social.updateMyProfile(payload)
        this.editHighlightDialog = false
        await this.loadProfile()
      } catch (error) {
        alert(error.response?.data?.message || "Speichern fehlgeschlagen.")
      } finally {
        this.actionLoading = false
        this.activeAction = null
      }
    },
    async fetchFriends() {
      this.friendsLoading = true
      try {
        this.friendsList = await api.social.getFriends(this.userId)
      } catch (error) {
        console.error("Fehler beim Laden der Freundesliste:", error)
      } finally {
        this.friendsLoading = false
      }
    },
    confirmDeletePost(postId) {
      if (window.confirm("Bist du sicher, dass du diesen Post endgültig löschen möchtest?")) {
        this.handleDeletePost(postId)
      }
    },
    async handleDeletePost(postId) {
      try {
        await api.social.deletePost(postId)
        this.userPosts = this.userPosts.filter((p) => p._id !== postId)
      } catch (error) {
        alert("Post konnte nicht gelöscht werden.")
      }
    },
    onViewProfile(userId) {
      this.$emit("view-profile", userId)
    },
    statusIcon(status) {
      const icons = { available: "mdi-check-circle-outline", in_training: "mdi-dumbbell", do_not_disturb: "mdi-minus-circle-outline" }
      return icons[status] || "mdi-help-circle-outline"
    },
    statusText(status) {
      const texts = { available: "Verfügbar", in_training: "Im Training", do_not_disturb: "Beschäftigt" }
      return texts[status] || "Unbekannt"
    },
    async loadProfile() {
      if (!this.userId) return
      this.loading = true
      try {
        const profileData = await api.social.getProfile(this.userId)
        this.profile = profileData
        if (profileData && !profileData.isLimited) {
          this.fetchUserPosts()
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.profileNotFound = true
        }
      } finally {
        this.loading = false
      }
    },
    async fetchUserPosts() {
      this.postsLoading = true
      try {
        this.userPosts = await api.social.getUserPosts(this.userId)
      } catch (error) {
        console.error("Fehler beim Laden der User-Posts:", error)
      } finally {
        this.postsLoading = false
      }
    },
    openCreateDialog() {
      this.profileData = {
        username: this.profile?.username || "",
        bio: "",
        interests: [],
        trainingGoals: [],
        favoriteEquipment: "",
        trainingTimes: "",
        lookingFor: "",
        status: "available",
        privacy: {
          postsVisibility: "friends",
          showAge: true,
          isSearchable: true,
          showInCheckinPool: true,
          allowTrainingRequests: true,
        },
      }
      this.profileDialog = true
    },
    openEditDialog() {
      if (!this.profile) return
      this.profileData = JSON.parse(
        JSON.stringify({
          username: this.profile.username,
          bio: this.profile.bio,
          interests: this.profile.interests,
          trainingGoals: this.profile.trainingGoals,
          favoriteEquipment: this.profile.favoriteEquipment,
          trainingTimes: this.profile.trainingTimes,
          lookingFor: this.profile.lookingFor,
          status: this.profile.status,
          privacy: this.profile.privacy || {
            postsVisibility: "friends",
            showAge: true,
            isSearchable: true,
            showInCheckinPool: true,
            allowTrainingRequests: true,
          },
        })
      )
      this.profileDialog = true
    },
    async submitProfile() {
      this.activeAction = "profile"
      this.actionLoading = true
      try {
        await api.social.updateMyProfile(this.profileData)
        this.profileDialog = false
        await this.loadProfile()
      } catch (error) {
        alert(error.response?.data?.message || "Ein Fehler ist aufgetreten.")
      } finally {
        this.actionLoading = false
        this.activeAction = null
      }
    },
  },
  watch: {
    userId: { immediate: true, handler: "loadProfile" },
    tab(newTab) {
      if (newTab === "friends") {
        this.fetchFriends()
      }
    },
  },
}
</script>

<style scoped>
/* Allgemeine Stile */
.fill-height {
  height: 100%;
  width: 100%;
}
.transparent {
  background-color: transparent !important;
}
.profile-container {
  max-width: 700px;
  margin: 0 auto;
  padding-bottom: 80px;
} /* Padding für Inhalt unter dem Deck */
.username-style {
  color: #c9eef2;
  font-weight: 500;
}

/* Statistiken-Stile */
.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 60px;
}
.stat .text-caption {
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Highlights Bubble Stile */
.highlights-container {
  display: flex;
  overflow-x: auto;
  padding: 4px 0 16px 0;
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.highlights-container::-webkit-scrollbar {
  display: none;
}
.highlight-bubble-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 20px;
  flex-shrink: 0;
  cursor: pointer;
}
.highlight-bubble {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background-color: #313131;
  border: 2px solid #4d4d4d;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease-out, border-color 0.2s ease;
}
.highlight-bubble-wrapper:hover .highlight-bubble {
  transform: scale(1.05);
  border-color: #c9eef2;
}
.highlight-bubble .v-icon {
  font-size: 32px !important;
}
.highlight-label {
  margin-top: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #f7f7f4;
  margin-bottom: 0;
}
.highlight-bubble.is-addable {
  background-color: transparent;
  border-style: dashed;
  border-width: 2px;
  border-color: #5a5a5a;
}
.highlight-bubble.is-addable .v-icon {
  font-size: 36px !important;
  color: #a0a0a0 !important;
}
.highlight-bubble-wrapper:hover .highlight-bubble.is-addable {
  border-color: #f7f7f4;
}
.highlight-bubble-wrapper:hover .highlight-bubble.is-addable .v-icon {
  color: #f7f7f4 !important;
}

/* Tab-Stile */
.content-tabs .v-tab {
  color: #f7f7f4a0;
  font-weight: 600;
  text-transform: none;
}
.content-tabs .v-tab--active {
  color: #f7f7f4;
}
.content-tabs .v-tabs-slider {
  background-color: #f7f7f4;
}

/* ====================================================== */
/* START: Angepasste Styles für das neue Action Deck      */
/* ====================================================== */
.action-deck {
  display: flex;
  justify-content: center; /* Zentriert die Buttons horizontal */
  gap: 12px; /* Abstand zwischen den Buttons */
  align-items: center;
}
.action-deck .v-btn {
  /* Buttons mit festem Icon-Stil */
  min-width: 0;
  width: 52px;
  height: 52px;
}
/* ====================================================== */
/* ENDE: Angepasste Styles                                */
/* ====================================================== */

/* Dialog Stile (für Vollständigkeit) */
.tiktok-dialog-container {
  background: transparent !important;
  box-shadow: none !important;
}
.tiktok-dialog-card {
  position: relative;
  background: rgba(40, 40, 40, 0.7);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.125);
  padding: 40px 24px;
  overflow: hidden;
}
.dialog-glow-border {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 24px;
  padding: 2px;
  width: 100%;
  background: linear-gradient(45deg, #f7b5a7, #c9eef2, #f7b5a7);
  background-size: 400% 400%;
  animation: gradient-animation 6s ease infinite;
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: destination-out;
  mask-composite: exclude;
  z-index: -1;
}
@keyframes gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
.close-button {
  position: absolute;
  top: 12px;
  right: 12px;
  background-color: rgba(0, 0, 0, 0.2);
}
.dialog-content .dialog-icon {
  font-size: 64px !important;
  color: #c9eef2;
  margin-bottom: 24px;
}
.dialog-content .dialog-title {
  font-size: 1rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}
.dialog-content .dialog-main-text {
  font-size: 1.5rem;
  font-weight: 600;
  color: #f7f7f4;
  line-height: 1.4;
  word-wrap: break-word;
  margin-bottom: 0;
}
.tiktok-dialog-card.edit-dialog {
  padding: 24px;
}
.edit-dialog-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #f7f7f4;
  margin-bottom: 24px;
  text-align: center;
}
.edit-dialog-content {
  margin-bottom: 24px;
}
.edit-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.styled-input.v-text-field--rounded > .v-input__control > .v-input__slot {
  background: rgba(0, 0, 0, 0.25) !important;
}
.styled-input .v-chip {
  background-color: #f7b5a7 !important;
  color: #232929 !important;
  font-weight: 600;
}

.crew-card-container {
  position: relative;
  background: rgba(30, 30, 30, 0.6); /* Dunkler, halbtransparenter Hintergrund */
  backdrop-filter: blur(15px) saturate(180%);
  -webkit-backdrop-filter: blur(15px) saturate(180%);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.125);
  padding: 20px;
  overflow: hidden; /* Wichtig für den Glow-Effekt */
}

/* Animierter Gradient als Glow-Effekt unter dem Rand */
.crew-card__glow {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 20px;
  padding: 2px;
  width: 100%;
  background: linear-gradient(45deg, #f7b5a7, #c9eef2, #f7b5a7);
  background-size: 400% 400%;
  animation: gradient-animation 6s ease infinite;
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  z-index: -1;
}

@keyframes gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.crew-card__content {
  position: relative;
  z-index: 1;
}

.crew-card__icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
}

.crew-card__icon {
  color: #c9eef2; /* Helle Akzentfarbe */
  font-size: 24px !important;
}

.crew-card__title {
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #f7f7f4;
}

.crew-card__subtitle {
  margin: 0;
  font-weight: 500;
  color: rgba(247, 247, 244, 0.7);
  line-height: 1;
}

.crew-card__divider {
  height: 1px;
  background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.2), transparent);
}

.crew-card__plan-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.crew-chip.v-chip {
  background-color: rgba(255, 255, 255, 0.1) !important;
  font-weight: 600;
  transition: all 0.2s ease-in-out;
}

.crew-chip.v-chip:hover {
  background-color: rgba(255, 255, 255, 0.2) !important;
  transform: translateY(-2px);
}
</style>
